import type { GovernedLibraryRemovalStatusResult } from "../api/cognitoryLibraryDeleteClient";

export type CognitoryLibraryDeleteFlowPhase =
  | "deleting"
  | "rebuilding_library"
  | "removed"
  | "failed";

export type RemovalUserVisiblePhase =
  | "deleting"
  | "rebuilding_library"
  | "removed"
  | "failed";

export const REMOVAL_POLL_INTERVAL_MS = 2000;
export const REMOVAL_POLL_MAX_ATTEMPTS = 120;

export const GOVERNED_LIBRARY_DELETE_FAILURE_MESSAGE =
  "Document removal did not complete successfully.";

export const GOVERNED_LIBRARY_DELETE_STILL_PROCESSING_MESSAGE =
  "Cognitory is still removing this document. The library will update automatically when deletion finishes.";

export const GOVERNED_LIBRARY_FINAL_DOCUMENT_MESSAGE =
  "This is the last eligible document in the Library and cannot be removed while serving is active.";

export function isTerminalRemovalUserVisiblePhase(
  phase: RemovalUserVisiblePhase,
): boolean {
  return phase === "removed" || phase === "failed";
}

export function mapRemovalStatusToFlowPhase(
  phase: RemovalUserVisiblePhase,
): CognitoryLibraryDeleteFlowPhase | null {
  if (phase === "deleting") {
    return "deleting";
  }
  if (phase === "rebuilding_library") {
    return "rebuilding_library";
  }
  if (phase === "removed") {
    return "removed";
  }
  if (phase === "failed") {
    return "failed";
  }
  return null;
}

export type RemovalStatusPollClassification =
  | { kind: "continue"; flowPhase?: CognitoryLibraryDeleteFlowPhase }
  | { kind: "removed" }
  | { kind: "failed"; message: string }
  | { kind: "poll_error" };

export function classifyRemovalStatusPoll(
  status: GovernedLibraryRemovalStatusResult,
): RemovalStatusPollClassification {
  if (status.kind === "status") {
    if (status.userVisiblePhase === "removed") {
      return { kind: "removed" };
    }
    if (status.userVisiblePhase === "failed") {
      return {
        kind: "failed",
        message: GOVERNED_LIBRARY_DELETE_FAILURE_MESSAGE,
      };
    }
    const flowPhase = mapRemovalStatusToFlowPhase(status.userVisiblePhase);
    return flowPhase ? { kind: "continue", flowPhase } : { kind: "poll_error" };
  }
  if (status.kind === "not_found") {
    return { kind: "removed" };
  }
  return { kind: "poll_error" };
}

export type PollRemovalUntilTerminalResult =
  | { kind: "removed" }
  | { kind: "failed"; message: string }
  | { kind: "still_processing"; message: string }
  | { kind: "aborted" };

export async function pollRemovalUntilTerminal(input: {
  cognitoryId: string;
  documentId: string;
  fetchStatus: (input: {
    cognitoryId: string;
    documentId: string;
  }) => Promise<GovernedLibraryRemovalStatusResult>;
  sleep: (ms: number) => Promise<void>;
  pollIntervalMs?: number;
  maxAttempts?: number;
  signal?: AbortSignal;
  onPhaseChange?: (phase: CognitoryLibraryDeleteFlowPhase) => void;
}): Promise<PollRemovalUntilTerminalResult> {
  const pollIntervalMs = input.pollIntervalMs ?? REMOVAL_POLL_INTERVAL_MS;
  const maxAttempts = input.maxAttempts ?? REMOVAL_POLL_MAX_ATTEMPTS;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (input.signal?.aborted) {
      return { kind: "aborted" };
    }

    const status = await input.fetchStatus({
      cognitoryId: input.cognitoryId,
      documentId: input.documentId,
    });
    const classification = classifyRemovalStatusPoll(status);

    if (classification.kind === "removed") {
      return { kind: "removed" };
    }
    if (classification.kind === "failed") {
      return { kind: "failed", message: classification.message };
    }
    if (
      classification.kind === "continue" &&
      classification.flowPhase &&
      classification.flowPhase !== "deleting"
    ) {
      input.onPhaseChange?.(classification.flowPhase);
    }

    if (input.signal?.aborted) {
      return { kind: "aborted" };
    }

    await input.sleep(pollIntervalMs);
  }

  return {
    kind: "still_processing",
    message: GOVERNED_LIBRARY_DELETE_STILL_PROCESSING_MESSAGE,
  };
}
